import React, { forwardRef, useCallback, useRef } from "react"
import {
  Breakpoint,
  FormControl,
  Input,
  PopoverOrigin,
  Theme,
  useMediaQuery
} from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import IconButton from "../IconButton"
import Popup, { PopupRenderCallback, PopupRenderProps } from "../Popup"
import CustomDrawer from "../CustomDrawer"
import WithOverlay from "../WithOverlay"
import { useStyles } from "./styles"

// Minimal shape required by SelectTrigger. Structurally compatible with
// reporting's FilterTypeItem so callers can pass it directly. Any extra fields
// (wide, type, etc.) are accepted but ignored here.
export type SelectTriggerFilter = {
  key: string
  label: string
  icon: string
}

export type SelectTriggerProps = {
  // Describes the trigger: drives the popover id (key), placeholder text
  // (label), and start icon (icon — FaIcon name).
  filter: SelectTriggerFilter
  // The selected value rendered inside the trigger. Falls back to
  // `filter.label` placeholder when not provided.
  value?: string
  // Show a vertical divider line between the value area and the caret.
  showLine?: boolean
  // Visually treat the trigger as focused. Only used when `children` is NOT
  // provided (e.g. Storybook). When `children` IS provided, Popup injects
  // aria-expanded which drives the focused visual via CSS.
  open?: boolean
  disabled?: boolean
  loading?: boolean
  size?: "small" | "medium"
  variant?: "outlined" | "filled"
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onClear?: () => void
  className?: string
  "data-automation-id"?: string
  ariaLabel?: string
  // When provided, the trigger renders a Popup anchored to itself and the
  // popover body is rendered from this children prop. Accepts either a
  // ReactNode or a render-prop receiving `{ open, onClose }`.
  children?: PopupRenderCallback | React.ReactNode
  popoverAnchorOrigin?: PopoverOrigin
  popoverTransformOrigin?: PopoverOrigin
}

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left"
}
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: "top",
  horizontal: "left"
}

const SelectTrigger = forwardRef<HTMLDivElement, SelectTriggerProps>(
  (
    {
      filter,
      value,
      showLine = false,
      open = false,
      disabled = false,
      loading = false,
      size = "medium",
      variant = "outlined",
      onClick,
      onClear,
      className,
      "data-automation-id": dataAutomationId,
      ariaLabel,
      children,
      popoverAnchorOrigin = DEFAULT_ANCHOR_ORIGIN,
      popoverTransformOrigin = DEFAULT_TRANSFORM_ORIGIN
    },
    ref
  ) => {
    const { classes, cx } = useStyles({ size, variant, showLine })

    // Below sm (also reached via high browser zoom) the anchored popover has no
    // room; open the body in a bottom drawer like reporting's FilterChip.
    const isMobile = useMediaQuery((theme: Theme) =>
      theme.breakpoints.down("sm" as Breakpoint)
    )

    const hasValue = value !== undefined && value !== null && value !== ""
    const formControlRef = useRef<HTMLDivElement | null>(null)
    const inputRef = useRef<HTMLInputElement | null>(null)

    // Size the readonly input to its content so a long value grows the trigger
    // instead of scrolling/truncating inside it. Floor at the placeholder length
    // so a short value doesn't shrink the trigger below its default width; +1
    // buffers proportional-font width so the text isn't clipped.
    const inputSizeChars =
      Math.max((value || "").length, filter.label.length) + 1

    const setFormControlRef = useCallback(
      (node: HTMLDivElement | null) => {
        formControlRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      // preventDefault so Popup ignores the click, since the aria-disabled trigger isn't natively disabled.
      if (disabled) {
        event.preventDefault()
        return
      }
      onClick?.(event)
    }

    const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return
      if (
        event.key === "Enter" ||
        event.key === " " ||
        event.key === "ArrowDown"
      ) {
        event.preventDefault()
        formControlRef.current?.click()
      }
    }

    // a11y: return focus to the trigger input before the clear button unmounts
    // (hasValue → false on clear removes it from the DOM), otherwise focus falls
    // to <body> and keyboard/SR users are stranded. WCAG 2.4.3.
    const focusTrigger = () => inputRef.current?.focus()

    const handleClearClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      onClear?.()
      focusTrigger()
    }

    const handleClearKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        event.stopPropagation()
        onClear?.()
        focusTrigger()
      }
    }

    // With children, Popup/WithOverlay inject aria-expanded onto this anchor,
    // which a bare div can't have (axe: aria-allowed-attr), so expose it as a
    // combobox — the role for a control that expands a listbox popup.
    const isPopupTrigger = !!children

    // popupOpen comes from Popup/WithOverlay via the anchor render-prop; for
    // the no-children path the SelectTrigger's `open` prop is used directly.
    const renderTriggerButton = (popupOpen: boolean) => (
      <FormControl
        ref={setFormControlRef}
        onClick={handleClick}
        className={cx(
          classes.root,
          // rootExpanded targets aria-expanded="true" (set by Popup when its
          // popover is open). Always-on; it's a no-op when the attribute isn't
          // present.
          classes.rootExpanded,
          { [classes.rootOpen]: popupOpen && !disabled },
          // aria-disabled (not native `disabled`) keeps the trigger focusable so
          // focus can return here when the popover closes mid-load (WCAG 2.4.3).
          { [classes.rootDisabled]: disabled },
          className
        )}
        data-automation-id={dataAutomationId}
        {...(isPopupTrigger && {
          role: "combobox",
          "aria-haspopup": "listbox",
          "aria-label": ariaLabel ?? filter.label
        })}
      >
        <Input
          disableUnderline
          readOnly
          inputRef={inputRef}
          value={hasValue ? value : ""}
          placeholder={filter.label}
          inputProps={{
            "aria-haspopup": "listbox",
            "aria-readonly": true,
            "aria-label": ariaLabel ?? filter.label,
            "aria-disabled": disabled || undefined,
            tabIndex: 0,
            size: inputSizeChars,
            onKeyDown: handleTriggerKeyDown
          }}
          startAdornment={
            filter.icon && (
              <span className={classes.startIcon}>
                <FaIcon solid name={filter.icon} size="inherit" />
              </span>
            )
          }
          endAdornment={
            <span className={classes.endAdornment}>
              {hasValue && onClear && !disabled && (
                <IconButton
                  size="xsmall"
                  onClick={handleClearClick}
                  onKeyDown={handleClearKeyDown}
                  aria-label={`Clear ${filter.label}`}
                  className={classes.clearButton}
                >
                  <FaIcon solid name="times-circle" />
                </IconButton>
              )}
              {loading ? (
                <FaIcon solid spin name="spinner-third" size="inherit" />
              ) : (
                <FaIcon
                  solid
                  size="inherit"
                  name={popupOpen ? "caret-up" : "caret-down"}
                />
              )}
            </span>
          }
        />
      </FormControl>
    )

    if (!children) return renderTriggerButton(open)

    const renderBody = ({ open: bodyOpen, onClose }: PopupRenderProps) =>
      typeof children === "function"
        ? (children as PopupRenderCallback)({ open: bodyOpen, onClose })
        : children

    if (isMobile && !disabled) {
      return (
        <WithOverlay
          id={filter.key}
          anchor={({ open: anchorOpen }) => renderTriggerButton(anchorOpen)}
        >
          {({ open: drawerOpen, onClose }) => {
            // a11y: return focus to the trigger input after the drawer
            // closes. Defer past MUI's slide-out (~225ms) — its focus trap
            // releases when the transition ends, so an earlier focus call
            // gets overridden.
            const handleClose = () => {
              onClose()
              window.setTimeout(() => inputRef.current?.focus(), 300)
            }
            return (
              <CustomDrawer
                open={drawerOpen}
                onClose={handleClose}
                anchor="bottom"
                size="lg"
                backdropVisible
              >
                {renderBody({ open: drawerOpen, onClose: handleClose })}
              </CustomDrawer>
            )
          }}
        </WithOverlay>
      )
    }

    return (
      <Popup
        id={filter.key}
        anchor={({ open: anchorOpen }) => renderTriggerButton(anchorOpen)}
        anchorOrigin={popoverAnchorOrigin}
        transformOrigin={popoverTransformOrigin}
        matchAnchorWidth
        classes={{ paper: classes.popoverPaper }}
      >
        {children}
      </Popup>
    )
  }
)

SelectTrigger.displayName = "SelectTrigger"

export default SelectTrigger
